// Supabase Primary Mathematics Service
// Fetches and transforms data from math_primarymaths table
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://eximyshafkuyehkiotuf.supabase.co';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV4aW15c2hhZmt1eWVoa2lvdHVmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTUxODc0MDEsImV4cCI6MjA3MDc2MzQwMX0.bjrI5MO_WUfnI53UaQ_m8BS8LPh2et5OrqMKmWUvYzw';

const supabase = createClient(supabaseUrl, supabaseKey);

// Cache for storing fetched data with timestamps
const dataCache = {};
// Disable caching completely in development to ensure fresh data always
// Set to 0 to disable caching completely during development
const CACHE_DURATION = process.env.NODE_ENV === 'development' ? 0 : 5 * 60 * 1000; // 0 in dev (no cache), 5 minutes in production

/**
 * Transform flat Supabase rows into nested structure matching mock data format
 * @param {Array} rows - Flat array of rows from Supabase
 * @returns {Object} - Nested structure: { grade, year, title, description, topics: [...] }
 */
const transformSupabaseDataToNested = (rows) => {
  if (!rows || rows.length === 0) return null;

  const firstRow = rows[0];
  const grade = firstRow.grade;
  
  // Group by topic_id
  const topicsMap = {};
  
  rows.forEach(row => {
    const topicId = row.topic_id;
    
    if (!topicsMap[topicId]) {
      topicsMap[topicId] = {
        id: topicId,
        code: row.topic_code,
        title: row.topic_title,
        description: row.topic_description,
        objectives: Array.isArray(row.objectives) ? row.objectives : [],
        sampleQuestions: []
      };
    }
    
    // Add question to topic
    topicsMap[topicId].sampleQuestions.push({
      id: row.question_id,
      question: row.question,
      answer: row.answer,
      solution: row.solution,
      difficulty: row.difficulty || 'basic',
      image: row.image || null
    });
  });
  
  // Convert map to array and sort by topic_id
  const topics = Object.values(topicsMap).sort((a, b) => {
    // Sort by topic_id (e.g., p1-1n1, p1-1n2, etc.)
    return a.id.localeCompare(b.id);
  });
  
  return {
    title: firstRow.title,
    description: firstRow.description,
    grade: firstRow.grade,
    year: firstRow.year,
    topics: topics
  };
};

/**
 * Test Supabase connection
 * @returns {Promise<boolean>} - True if connection is successful
 */
export const testSupabaseConnection = async () => {
  try {
    const { data, error } = await supabase
      .from('math_primarymaths')
      .select('grade')
      .limit(1);
    
    if (error) {
      console.error('Supabase connection test failed:', error);
      return false;
    }
    
    console.log('Supabase connection successful');
    return true;
  } catch (error) {
    console.error('Supabase connection error:', error);
    return false;
  }
};

/**
 * Primary Mathematics Service
 */
export const primaryMathsService = {
  /**
   * Get all data for a specific grade (P1, P2, P3, P4, P5, P6)
   * @param {string} grade - Grade identifier (e.g., 'P1', 'P2')
   * @returns {Promise<Object|null>} - Grade data with topics and questions
   */
  async getGradeData(grade, forceRefresh = false) {
    try {
      // Check cache first (only if not forcing refresh and cache is enabled)
      if (!forceRefresh && CACHE_DURATION > 0 && dataCache[grade]) {
        const cached = dataCache[grade];
        const now = Date.now();
        // Return cached data if it's still fresh (within cache duration)
        if (cached.timestamp && (now - cached.timestamp) < CACHE_DURATION) {
          return cached.data;
        } else {
          // Cache expired, remove it
          delete dataCache[grade];
        }
      } else if (forceRefresh || CACHE_DURATION === 0) {
        // Clear cache when forcing refresh or cache is disabled
        delete dataCache[grade];
      }

      const { data, error } = await supabase
        .from('math_primarymaths')
        .select('*')
        .eq('grade', grade)
        .order('id', { ascending: true }) // Order by id first to get the actual first inserted row
        .order('topic_id', { ascending: true })
        .order('question_id', { ascending: true });

      if (error) {
        console.error(`Supabase query error for grade ${grade}:`, error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.warn(`No data found for grade ${grade} in Supabase`);
        return null;
      }

      const transformedData = transformSupabaseDataToNested(data);
      
      // Cache the result with timestamp (only if caching is enabled)
      if (transformedData && CACHE_DURATION > 0) {
        dataCache[grade] = {
          data: transformedData,
          timestamp: Date.now()
        };
      }
      
      // Log for debugging in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`Fetched fresh data for ${grade}:`, {
          topics: transformedData?.topics?.length || 0,
          description: transformedData?.description,
          firstTopic: transformedData?.topics?.[0]?.title
        });
      }
      
      return transformedData;
    } catch (error) {
      console.error(`Error fetching grade data for ${grade}:`, error);
      return null;
    }
  },

  /**
   * Get all available grades
   * @returns {Promise<Array<string>>} - Array of grade identifiers
   */
  async getAvailableGrades() {
    try {
      const { data, error } = await supabase
        .from('math_primarymaths')
        .select('grade')
        .order('grade', { ascending: true });

      if (error) throw error;

      const uniqueGrades = [...new Set(data.map(row => row.grade))];
      return uniqueGrades.sort();
    } catch (error) {
      console.error('Error fetching available grades:', error);
      return [];
    }
  },

  /**
   * Get topics for a specific grade
   * @param {string} grade - Grade identifier
   * @param {boolean} forceRefresh - Force refresh from Supabase
   * @returns {Promise<Array>} - Array of topics
   */
  async getTopicsByGrade(grade, forceRefresh = false) {
    try {
      const gradeData = await this.getGradeData(grade, forceRefresh);
      return gradeData ? gradeData.topics : [];
    } catch (error) {
      console.error(`Error fetching topics for grade ${grade}:`, error);
      return [];
    }
  },

  /**
   * Get all questions for a specific grade
   * @param {string} grade - Grade identifier
   * @returns {Promise<Array>} - Array of questions with topic info
   */
  async getAllQuestionsByGrade(grade) {
    try {
      const topics = await this.getTopicsByGrade(grade);
      const allQuestions = [];
      
      topics.forEach(topic => {
        topic.sampleQuestions.forEach(question => {
          allQuestions.push({
            ...question,
            topicId: topic.id,
            topicCode: topic.code,
            topicTitle: topic.title,
            grade: grade
          });
        });
      });
      
      return allQuestions;
    } catch (error) {
      console.error(`Error fetching all questions for grade ${grade}:`, error);
      return [];
    }
  },

  /**
   * Get questions by difficulty level
   * @param {string} difficulty - Difficulty level ('basic', 'intermediate', 'advanced')
   * @param {string|null} grade - Optional grade filter
   * @returns {Promise<Array>} - Array of questions
   */
  async getQuestionsByDifficulty(difficulty, grade = null) {
    try {
      const questions = grade
        ? await this.getAllQuestionsByGrade(grade)
        : await this.getAllQuestions();

      return questions.filter(q => q.difficulty === difficulty);
    } catch (error) {
      console.error('Error fetching questions by difficulty:', error);
      return [];
    }
  },

  /**
   * Get all questions across all grades
   * @returns {Promise<Array>} - Array of all questions
   */
  async getAllQuestions() {
    try {
      const grades = await this.getAvailableGrades();
      const allQuestions = [];

      for (const grade of grades) {
        const questions = await this.getAllQuestionsByGrade(grade);
        allQuestions.push(...questions);
      }

      return allQuestions;
    } catch (error) {
      console.error('Error fetching all questions:', error);
      return [];
    }
  },

  /**
   * Get statistics for a grade or all grades
   * @param {string|null} grade - Optional grade filter
   * @returns {Promise<Object>} - Statistics object
   */
  async getStats(grade = null) {
    try {
      if (grade) {
        const gradeData = await this.getGradeData(grade);
        if (!gradeData) return null;

        const totalTopics = gradeData.topics.length;
        const totalQuestions = gradeData.topics.reduce(
          (sum, topic) => sum + topic.sampleQuestions.length,
          0
        );
        const difficultyBreakdown = gradeData.topics.reduce((acc, topic) => {
          topic.sampleQuestions.forEach(q => {
            acc[q.difficulty] = (acc[q.difficulty] || 0) + 1;
          });
          return acc;
        }, {});

        return {
          [grade]: {
            totalTopics,
            totalQuestions,
            difficultyBreakdown
          }
        };
      } else {
        // Get stats for all grades
        const grades = await this.getAvailableGrades();
        const stats = {};

        for (const g of grades) {
          const gradeStats = await this.getStats(g);
          if (gradeStats) {
            Object.assign(stats, gradeStats);
          }
        }

        return stats;
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      return {};
    }
  },

  /**
   * Clear cache for a specific grade or all grades
   * @param {string|null} grade - Optional grade to clear, or null to clear all
   */
  clearCache(grade = null) {
    if (grade) {
      delete dataCache[grade];
    } else {
      Object.keys(dataCache).forEach(key => delete dataCache[key]);
    }
  },

  /**
   * Force refresh data for a specific grade (bypasses cache)
   * @param {string} grade - Grade identifier
   * @returns {Promise<Object|null>} - Fresh grade data
   */
  async refreshGradeData(grade) {
    return await this.getGradeData(grade, true);
  }
};

export default primaryMathsService;

